import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import type { OffsetPageQueryDTO } from "@/shared/contracts/base";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { confirm } from "@devhop/ui";
import { useNavigate, useSearch } from "@tanstack/react-router";
import type { RowSelectionState } from "@tanstack/react-table";
import { useCallback, useState } from "react";
import type { ListingProductListItem } from "../api/client";
import {
  useBulkUpdateListingsMutation,
  useListingProductsQuery,
} from "../api/queries";
import { ListingProductsTable } from "../ui/ListingProductsTable";
import { ListingsFilter } from "../ui/ListingsFilter";
import { ListingsToolbar } from "../ui/ListingsToolbar";

export function ListingsPage() {
  const nav = useNavigate({ from: "/app/moderation" });
  const search = useSearch({
    from: "/app/moderation",
  }) as OffsetPageQueryDTO & {
    q?: string;
    recentDays?: number;
  };

  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [tableKey, setTableKey] = useState(0);

  const bulkUpdate = useBulkUpdateListingsMutation();

  const list = useListingProductsQuery({
    limit: search.limit ?? 20,
    offset: search.offset ?? 0,
    sort: search.sort,
    filters: search.filters,
    q: search.q,
    recentDays: search.recentDays,
  });

  const data = list.data?.data ?? [];
  const meta = list.data?.meta ?? { total: 0, limit: 20, offset: 0 };

  const selectedCount = Object.values(rowSelection).filter(Boolean).length;

  const clearSelection = useCallback(() => {
    setRowSelection({});
    setTableKey((k) => k + 1);
  }, []);

  const handleBulkHide = useCallback(
    async (ids: string[]) => {
      const ok = await confirm({
        title: "ເຊື່ອງລາຍການສິນຄ້າ",
        description: `ທ່ານແນ່ໃຈບໍ່ວ່າຈະເຊື່ອງ ${ids.length} listing? ລາຍການທີ່ເຊື່ອງຈະບໍ່ສະແດງບນ marketplace ແຕ່ຍັງເປີດຄືນໄດ້.`,
        actionText: "ເຊື່ອງ",
        ActionProps: { variant: "default" },
      });
      if (!ok) return;
      await bulkUpdate.mutateAsync({ ids, status: "hidden" });
      clearSelection();
    },
    [bulkUpdate, clearSelection],
  );

  const handleBulkRemove = useCallback(
    async (ids: string[]) => {
      const ok = await confirm({
        title: "ລຶບລາຍການສິນຄ້າ",
        description: `ທ່ານແນ່ໃຈບໍ່ວ່າຈະລຶບ ${ids.length} listing? ລາຍການທີ່ລຶບຈະບໍ່ສະແດງບນ marketplace.`,
        actionText: "ລຶບ",
        ActionProps: { variant: "destructive" },
      });
      if (!ok) return;
      await bulkUpdate.mutateAsync({ ids, status: "removed" });
      clearSelection();
    },
    [bulkUpdate, clearSelection],
  );

  const handleBulkRestore = useCallback(
    async (ids: string[]) => {
      await bulkUpdate.mutateAsync({ ids, status: "active" });
      clearSelection();
    },
    [bulkUpdate, clearSelection],
  );

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            { label: "ແຜງຄວບຄຸມ", to: "/app/dashboard" },
            { label: "ການກວດກາ Marketplace" },
          ]}
        />
      </Header>

      <Main>
        <ListingsToolbar />

        <div className="flex flex-col rounded-xl border bg-card pt-2">
          <ListingsFilter />

          <ListingProductsTable
            isLoading={list.isLoading}
            data={data}
            offset={meta.offset}
            limit={meta.limit}
            totalCount={meta.total}
            sortBy={search.sort?.[0]?.field}
            sortOrder={search.sort?.[0]?.dir}
            onView={(row: ListingProductListItem) =>
              nav({
                to: "/app/moderation/listings/products/$catalogProductId",
                params: { catalogProductId: row.catalogProductId },
              })
            }
            onPaginationChange={(offset, limit) =>
              nav({ search: { ...search, offset, limit } })
            }
            onSortingChange={(id, desc) =>
              nav({
                search: {
                  ...search,
                  offset: 0,
                  sort: [{ field: id, dir: desc ? "desc" : "asc" }],
                },
              })
            }
            rowSelection={rowSelection}
            onRowSelectionChange={setRowSelection}
            selectedCount={selectedCount}
            onClearSelection={clearSelection}
            onBulkHide={handleBulkHide}
            onBulkRemove={handleBulkRemove}
            onBulkRestore={handleBulkRestore}
            tableKey={tableKey}
          />
        </div>
      </Main>
    </>
  );
}
